// Enhanced Tournament notifications with dual submission support and schedule requests
import React, { useState, useCallback, useEffect } from 'react';
import { useTournaments } from '../../../context/TournamentContext';
import { useAuth } from '../../../context/AuthContext';
import MatchResultForm from './MatchResultForm';
import SubmissionComparisonModal from './SubmissionComparisonModal';
import { enhancedSubscriptionManager } from '../../../context/shared/enhancedSubscriptionManager';
import toast from 'react-hot-toast';

const TournamentNotifications = () => {
  const { user } = useAuth();
  const {
    loadPendingTournamentSubmissions,
    approveTournamentResult,
    submitTournamentResult,
    getMatchById
  } = useTournaments();

  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [, setLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [, setHideNotifications] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // NEW: State for modification functionality
  const [showModificationForm, setShowModificationForm] = useState(false);
  const [modificationData, setModificationData] = useState(null);

  // Load pending submissions for tournament owner (enhanced for dual submissions)
  const loadSubmissions = useCallback(async () => {
    if (!user?.id) {
      return;
    }
    setLoading(true);
    try {
      const result = await loadPendingTournamentSubmissions();
      if (result.success) {
        // The new backend function returns aggregated submissions with dual team data
        const transformedSubmissions = result.data.map(submission => ({
          ...submission,
          // Add action type for compatibility with shared component
          action_type: 'pending_approval',
          // Transform for notification display
          team_name: `${submission.team1_name} vs ${submission.team2_name}`,
          opponent_name: '',
          match_data: {
            team1_score: submission.team1_submission_data?.team1_score || submission.team2_submission_data?.team1_score || 0,
            team2_score: submission.team1_submission_data?.team2_score || submission.team2_submission_data?.team2_score || 0,
            team1_screenshot_url: submission.team1_submission_data?.team1_screenshot_url,
            team2_screenshot_url: submission.team2_submission_data?.team2_screenshot_url,
            // Include submission status for UI display
            submission_status: submission.submission_status,
            submissions_match: submission.submissions_match
          }
        }));
        
        setPendingSubmissions(transformedSubmissions);
      } else {
        setPendingSubmissions([]);
      }
    } catch (error) {
      console.error('Error loading pending tournament submissions:', error);
      setPendingSubmissions([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, loadPendingTournamentSubmissions]);
  
  // Combined data loading
  const loadAllData = useCallback(async () => {
    await loadSubmissions();
  }, [loadSubmissions]);

  // Handle viewing submission details (enhanced for dual submissions)
  const handleViewMatch = useCallback(async (matchId) => {
    try {
      // Find the corresponding submission data
      const submission = pendingSubmissions.find(s => s.match_id === matchId);
      
      if (!submission) {
        console.error('No submission found for match ID:', matchId);
        return;
      }
      
      setSelectedSubmission(submission);
      setShowComparisonModal(true);
      setHideNotifications(true);
    } catch (error) {
      console.error('Error opening submission comparison:', error);
    }
  }, [pendingSubmissions]);

  // Handle modal close
  const handleCloseModal = useCallback((actionTaken = false) => {
    setShowComparisonModal(false);
    setSelectedSubmission(null);
    // Only restore notifications visibility if no action was taken
    if (!actionTaken) {
      setHideNotifications(false);
    }
  }, []);

  // Handle approval from comparison modal
  const handleApprove = useCallback(async (submissionId, selectedSubmissionId = null) => {
    if (!submissionId) return;
    
    setIsProcessing(true);
    try {
      const result = await approveTournamentResult(submissionId, selectedSubmissionId);
      if (result.success) {
        // Show success toast
        const submission = selectedSubmission;
        toast.success(`Match result approved! ${submission.team1_name} vs ${submission.team2_name}`, {
          duration: 4000,
          icon: '✅'
        });
        
        // Remove from pending list immediately for instant UI feedback
        setPendingSubmissions(prev => prev.filter(s => s.id !== submissionId));
        handleCloseModal(true);
        
        // Reload submissions to ensure consistency
        setTimeout(() => {
          loadSubmissions();
        }, 1000);
      } else {
        toast.error(result.error || 'Failed to approve match result', {
          duration: 4000,
          icon: '❌'
        });
      }
    } catch (error) {
      console.error('Error approving submission:', error);
      toast.error('An unexpected error occurred while approving the match', {
        duration: 4000,
        icon: '❌'
      });
    } finally {
      setIsProcessing(false);
    }
  }, [approveTournamentResult, selectedSubmission, handleCloseModal, loadSubmissions]);

  // NEW: Handle modification requests
  const handleModify = useCallback(async (submissionId, mode, initialData) => {
    console.log('Handling modification:', { submissionId, mode, initialData });
    
    // Find the submission and associated match
    const submission = pendingSubmissions.find(s => s.id === submissionId);
    if (!submission) {
      console.error('Submission not found for modification:', submissionId);
      return;
    }

    try {
      // Load full match data with team members
      console.log('Loading full match data for:', submission.match_id);
      const matchResult = await getMatchById(submission.match_id);
      
      if (!matchResult.success) {
        console.error('Failed to load match data:', matchResult.error);
        toast.error('Failed to load match data for modification');
        return;
      }

      const fullMatch = matchResult.data;
      console.log('Full match data loaded:', fullMatch);

      // Create match object for MatchResultForm with full team data
      const matchForForm = {
        ...fullMatch, // Include all original match data
        // Mark as modification to trigger proper form mode
        isModification: true,
        // Add suggested data based on initial data
        suggestedTeam1Score: initialData?.team1_score,
        suggestedTeam2Score: initialData?.team2_score,
        suggestedMvpId: initialData?.mvp_player_id || initialData?.mvpPlayerId,
        suggestedPlayerStats: initialData?.player_stats || initialData?.playerStats || [],
        team1_vod_url: initialData?.team1_vod_url || '',
        team2_vod_url: initialData?.team2_vod_url || ''
      };

      setModificationData({
        match: matchForForm,
        initialData,
        mode,
        submissionId
      });
      
      setShowComparisonModal(false);
      setShowModificationForm(true);
      setHideNotifications(true);

    } catch (error) {
      console.error('Error loading match data for modification:', error);
      toast.error('Failed to load match data for modification');
    }
  }, [pendingSubmissions, getMatchById]);

  // Handle modification form save
  const handleModificationSave = useCallback(async (matchId, resultData) => {
    console.log('Saving modification:', { matchId, resultData });
    
    try {
      setIsProcessing(true);
      
      // Find the original submission data
      const submission = pendingSubmissions.find(s => s.match_id === matchId);
      if (!submission) {
        throw new Error('Could not find submission data for match');
      }
      
      // Submit the modified result as tournament owner
      // Use the first team ID (tournament owner can submit for either team)
      const teamId = submission.team1_id;
      console.log('Submitting modified result with teamId:', teamId);
      
      const result = await submitTournamentResult(matchId, teamId, resultData);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save modification');
      }
      
      // After successful submission, immediately approve it to finalize the match
      console.log('Auto-approving tournament owner modification...');
      const approvalResult = await approveTournamentResult(submission.id, null, 'Tournament owner modification - auto-approved');
      
      if (!approvalResult.success) {
        console.warn('Failed to auto-approve modification, but submission was successful');
      }
      
      toast.success('Match result has been modified and finalized', {
        duration: 4000,
        icon: '✅'
      });
      
      // Remove from pending list immediately for instant UI feedback
      setPendingSubmissions(prev => prev.filter(s => s.id !== submission.id));
      
      setShowModificationForm(false);
      setModificationData(null);
      setHideNotifications(false);
      
      // Reload submissions to ensure consistency
      setTimeout(() => {
        loadSubmissions();
      }, 1000);
      
    } catch (error) {
      console.error('Error saving modification:', error);
      toast.error('Failed to save modification', {
        duration: 4000,
        icon: '❌'
      });
    } finally {
      setIsProcessing(false);
    }
  }, [loadSubmissions, pendingSubmissions, submitTournamentResult, approveTournamentResult]);

  // Handle modification form close
  const handleModificationClose = useCallback(() => {
    setShowModificationForm(false);
    setModificationData(null);
    setHideNotifications(false);
  }, []);




  // Schedule requests/notifications now live in the notifications bell
  // (useScheduleRequests). When the DB changes, tell the bell to refresh.
  const handleScheduleUpdate = useCallback(() => {
    window.dispatchEvent(new Event('scheduleActionUpdate'));
  }, []);

  // Real-time subscription setup for dual submissions and schedule requests
  useEffect(() => {
    if (!user?.id) return;

    const subscriptionKey = `tournament_notifications_${user.id}`;

    // Listen for tournament submission updates
    const handleTournamentSubmissionUpdate = (data) => {
      // Reload submissions immediately for real-time updates
      loadSubmissions();
      
      // Also emit custom event for other components to listen to
      window.dispatchEvent(new CustomEvent('tournamentActionUpdate', { 
        detail: data 
      }));
    };

    // Subscribe to tournament_match_submissions changes (new aggregation table)
    enhancedSubscriptionManager.subscribe(
      subscriptionKey,
      {
        event: {
          event: '*',
          schema: 'public',
          table: 'tournament_match_submissions'
        },
        rateLimitMs: 2000 // 2 second rate limit for real-time notifications
      },
      handleTournamentSubmissionUpdate
    );

    // Also subscribe to individual tournament_match_actions for immediate updates
    enhancedSubscriptionManager.subscribe(
      `${subscriptionKey}_actions`,
      {
        event: {
          event: '*',
          schema: 'public',
          table: 'tournament_match_actions'
        },
        rateLimitMs: 2000
      },
      handleTournamentSubmissionUpdate
    );

    // Subscribe to schedule request updates
    enhancedSubscriptionManager.subscribe(
      `${subscriptionKey}_schedule`,
      {
        event: {
          event: '*',
          schema: 'public',
          table: 'match_schedule_requests'
        },
        rateLimitMs: 2000
      },
      handleScheduleUpdate
    );

    // Cleanup subscriptions on unmount
    return () => {
      enhancedSubscriptionManager.unsubscribe(subscriptionKey);
      enhancedSubscriptionManager.unsubscribe(`${subscriptionKey}_actions`);
      enhancedSubscriptionManager.unsubscribe(`${subscriptionKey}_schedule`);
    };
  }, [user?.id, loadSubmissions, handleScheduleUpdate]);

  // Initial load on mount
  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // Open the submission review modal from the header bell (decoupled via a window event)
  useEffect(() => {
    const handler = (e) => {
      const matchId = e?.detail?.matchId;
      if (matchId) handleViewMatch(matchId);
    };
    window.addEventListener('openTournamentSubmissionReview', handler);
    return () => window.removeEventListener('openTournamentSubmissionReview', handler);
  }, [handleViewMatch]);

  return (
    <>
      {/* Floating popup removed — pending submissions now surface in the header bell.
          The review modal below is opened from the bell via the 'openTournamentSubmissionReview' event. */}

      {/* Submission Comparison Modal */}
      {showComparisonModal && selectedSubmission && (
        <SubmissionComparisonModal
          submission={selectedSubmission}
          onApprove={handleApprove}
          onModify={handleModify}
          onClose={() => handleCloseModal(false)}
          isLoading={isProcessing}
        />
      )}

      {/* Modification Form Modal */}
      {showModificationForm && modificationData && (
        <MatchResultForm
          match={modificationData.match}
          initialData={modificationData.initialData}
          modificationMode={modificationData.mode}
          onClose={handleModificationClose}
          onSave={handleModificationSave}
          type="tournament"
          mode="tournament_owner_modification"
        />
      )}
    </>
  );
};

export default TournamentNotifications;